/**
 * Keeps the parts of an inline "Pairs Well With" form that sit outside the option
 * list — title, link, image, price — in sync with the selected variant.
 * variant:changed comes from the product-form-options inside it.
 *
 * A grouped product's options span several products, so the title and link
 * follow the variant's own product rather than a fixed one.
 */
class PairsWellCard extends HTMLElement {
  constructor() {
    super();

    // Bound here rather than in connectedCallback so that being moved in the
    // DOM can't attach a second listener.
    this.addEventListener("variant:changed", (e) => {
      const { variant } = e.detail;

      if (!variant || variant.id === this.renderedVariantId) return;

      this.updateContent(variant);
      this.renderedVariantId = variant.id;
    });
  }

  updateContent(variant) {
    // productUrl rather than the product's url helper, so a localised market's
    // root path survives the update.
    const url = `${window.productUrl(
      variant.product.handle,
    )}?variant=${window.gidToId(variant.id)}`;

    this.links.forEach((link) => {
      link.href = url;
    });

    this.titleEls.forEach((titleEl) => {
      titleEl.textContent = variant.product.title;
    });

    this.imageEls.forEach((imageEl) => {
      if (!variant.image) {
        imageEl.style.display = "none";
        return;
      }

      const src = window.getSizedImageUrl(variant.image.url, "x312");

      imageEl.style.display = null;
      imageEl.alt =
        variant.image.altText || `${variant.product.title} ${variant.title}`;
      imageEl.src = src;
      imageEl.srcset = src;
    });

    this.priceEls.forEach((priceEl) => {
      priceEl.setAttribute("price", variant.price.amount);
      priceEl.setAttribute(
        "compare-at-price",
        variant.compareAtPrice?.amount || 0,
      );
      priceEl.setAttribute("currency", variant.price.currencyCode);
      priceEl.setAttribute("dynamic", "");
    });
  }

  // Falls back to the attribute so the server-rendered variant is the starting
  // point; attributes can't be read in the constructor.
  get renderedVariantId() {
    return this._renderedVariantId ?? this.getAttribute("rendered-variant-id");
  }

  set renderedVariantId(id) {
    this._renderedVariantId = id;
  }

  get links() {
    return this.querySelectorAll("[data-pairs-well-link]");
  }

  get titleEls() {
    return this.querySelectorAll("[data-pairs-well-title]");
  }

  get imageEls() {
    return this.querySelectorAll(".pairs-well__image");
  }

  get priceEls() {
    return this.querySelectorAll("price-snippet");
  }
}

if (!customElements.get("pairs-well-card")) {
  customElements.define("pairs-well-card", PairsWellCard);
}
